import { ErrorSnackbar } from '@application/components';
import { FeedbackContext, UserContext } from '@application/contexts';
import { extractErrorCodes } from '@application/utils/urql-utils';
import { SiteDataView } from '@domain/graphql.types';
import { useSitesQuery } from '@domain/index';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SiteLookupContext from '../context/SiteLookupContext';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import { SiteLookupFilters } from '../filters/useSiteLookupFilters';
import SiteLookupList from './SiteLookupList';

type UseSiteLookupListProps = {
  filters: SiteLookupFilters;
};

const useSiteLookupList = ({ filters }: UseSiteLookupListProps) => {
  const { setHasSitesIncludedInReport, setSelectedSite, setSearchLimit } = useContext(SiteLookupMapContext);
  const { dataView } = useContext(SiteLookupContext);
  const { setFeedback } = useContext(FeedbackContext);
  const { refreshUser } = useContext(UserContext);
  const { t } = useTranslation();

  const { data, error, reexecuteQuery, fetching } = useSitesQuery({
    variables: {
      input: {
        coordinates: filters.address?.coordinates || { latitude: 0, longitude: 0 },
        range: filters.radius,
        stateCode: filters.address?.regionCode || '',
        viewMode: dataView === 'historical' ? SiteDataView.Historical : SiteDataView.Realtime,
      },
    },
    pause: true,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: execute only when wanted filters update
  useEffect(() => {
    // We can either have the full address OR/AND the coordinates. The coordinates
    // could be present without any address being typed/selected, if the user selects
    // a point on the map.
    if (!filters.address?.fullAddress && !filters.address?.coordinates) {
      return;
    }

    setHasSitesIncludedInReport(true);
    setSearchLimit(null);
    setSelectedSite(null);
    reexecuteQuery();
  }, [dataView, filters.address, filters.radius]);

  useEffect(() => {
    const errors = extractErrorCodes(error);
    const isRateLimited = errors.some((e) => e.status === 429) || error?.graphQLErrors?.some((e) => e.message.includes('Rate limit exceeded'));

    if (isRateLimited) {
      setFeedback(<ErrorSnackbar message={t('siteLookup.error.rateLimitExceeded')} onClose={() => setFeedback(null)} />);
    }
  }, [error, setFeedback, t]);

  useEffect(() => {
    if (!data?.sites.results) {
      return;
    }

    setSearchLimit({
      count: data.sites.limit,
      exceeded: data.sites.limitExceeded,
    });
    setHasSitesIncludedInReport(data.sites.includedInReportCount > 0);

    // A completed search consumes one of the tenant's daily searches, so refresh
    // the user to update the remaining searches count shown in the banner.
    refreshUser();
  }, [data, setHasSitesIncludedInReport, setSearchLimit, refreshUser]);

  const List = useMemo(
    () => () => (
      <SiteLookupList
        dataView={dataView}
        hasSearchResult={!!data?.sites.results}
        fetching={fetching}
        sites={data?.sites.results || []}
        totalCount={data?.sites.stationsCount || 0}
        trackedPortsCount={data?.sites.trackedPortsCount || 0}
        aggregateData={data?.sites.aggregateData}
      />
    ),
    [data, dataView, fetching],
  );

  return {
    data: {
      sites: data?.sites.results || [],
      totalCount: data?.sites.stationsCount || 0,
    },
    List,
    state: {
      fetching,
    },
  };
};

export default useSiteLookupList;
